import html2pdf from 'html2pdf.js';
import { useCallback } from 'react';

export const useChatPdfExport = () => {
  const exportChatToPdf = useCallback(async (element: HTMLElement | null, title: string = 'Chat Export') => {
    if (!element) return;

    // Store container reference outside try block so it's accessible in finally
    let container: HTMLDivElement | null = null;

    try {
      // Clone the element to manipulate it for PDF generation without affecting UI
      const clone = element.cloneNode(true) as HTMLElement;

      // Reset scroll and height styles on the clone to capture full content
      clone.style.height = 'auto';
      clone.style.overflow = 'visible';
      clone.style.maxHeight = 'none';
      clone.style.width = '100%'; // Ensure full width
      clone.style.background = '#FFFFFF'; // Ensure white background
      clone.style.color = '#000000'; // Ensure text is visible
      
      // 1. Remove all Duty Calculator instances
      const dutyCalculators = clone.querySelectorAll('.inline-duty-calculator-root');
      dutyCalculators.forEach(el => {
        el.remove();
      });

      // 2. Expand all Thinking/Reasoning blocks
      const thinkingRoots = clone.querySelectorAll('.thinking-display-root');
      thinkingRoots.forEach((root) => {
        // Find the Collapse component (or wrapper) and force expansion
        const collapse = root.querySelector('.thinking-display-collapse') as HTMLElement;
        if (collapse) {
          collapse.style.height = 'auto';
          collapse.style.overflow = 'visible';
          collapse.style.display = 'block';
          collapse.style.visibility = 'visible';
          // Remove any inline styles that might hide it (MUI transition styles)
          collapse.style.minHeight = 'auto';
        }
        
        // Also ensure the content inside is visible
        const content = root.querySelector('.thinking-display-content') as HTMLElement;
        if (content) {
          content.style.display = 'block';
          content.style.visibility = 'visible';
        }

        // Expand the inner scrolling list
        const thinkingList = root.querySelector('.thinking-display-list') as HTMLElement;
        if (thinkingList) {
          thinkingList.style.maxHeight = 'none';
          thinkingList.style.overflow = 'visible';
          thinkingList.style.height = 'auto';
        }

        // Hide the expand/collapse toggle button to keep it clean
        const toggleBtn = root.querySelector('button');
        if (toggleBtn) {
          toggleBtn.style.display = 'none';
        }
      });

      // Create a container for the clone
      container = document.createElement('div');
      container.style.position = 'absolute';
      // Position off-screen but in a way that html2canvas can render it
      container.style.left = '-9999px';
      container.style.top = '0';
      // Set a fixed width for the PDF generation container to ensure consistent layout
      // 800px is roughly A4 width at web resolution (96dpi ~ 8.27in)
      container.style.width = '800px'; 
      container.style.zIndex = '-1000';
      container.appendChild(clone);
      
      document.body.appendChild(container);

      // Small delay to ensure DOM update
      await new Promise(resolve => setTimeout(resolve, 100));

      const opt = {
        margin: [0.5, 0.5, 0.5, 0.5] as [number, number, number, number],
        filename: `${title.replace(/[^a-z0-9]/gi, '_')}_${new Date().toISOString().split('T')[0]}.pdf`,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { 
          scale: 2, 
          useCORS: true,
          backgroundColor: '#ffffff',
          logging: false,
          allowTaint: false,
          foreignObjectRendering: false,
          windowWidth: 800,
          scrollY: 0, 
        },
        jsPDF: { unit: 'in', format: 'letter', orientation: 'portrait' },
        pagebreak: { mode: ['avoid-all', 'css', 'legacy'] }
      };

      await html2pdf().set(opt).from(clone).save();

    } catch (e) {
      console.error('PDF Export failed', e);
    } finally {
      // Cleanup: remove the container using the stored reference
      if (container && container.parentNode) {
        container.parentNode.removeChild(container);
      }
    }
  }, []);

  return { exportChatToPdf };
};
